import { Navigate, type RouteObject } from "react-router";
import { ROUTES } from "@/shared/constants/routes";

/**
 * The Staff workspace's routes (M19, contracts §11), in a file of their own so the one route table stays
 * under its size backstop (rules/01 §9) — `index.tsx` mounts this under the shell in one line.
 */
export const staffRoute: RouteObject = {
  /**
   * One layout route for the whole workspace: `StaffWorkspaceGate` answers the add-on question once —
   * the entitlements, or any staff read refused with `402 ADDON_REQUIRED` — and shows `AddonGate` in
   * the page's place, so no Staff page has to ask. It is lazy like every page, so the gate (and the
   * purchase panel behind it) loads only here.
   *
   * Every page is a file in its owning lane's folder (`src/modules/staff/<area>/pages/`); a lane
   * replaces the file and never edits this table. Static segments are written before the
   * parameter beside them (`employees/new` before `employees/:personId`), the way they resolve.
   */
  path: "staff",
  lazy: async () => ({
    Component: (await import("@/modules/staff/shared/StaffWorkspaceGate")).StaffWorkspaceGate,
  }),
  children: [
    {
      // The Staff Dashboard (renamed from Today on Kumar's word, 2026-10-01 22:55). The address stays `/staff`.
      index: true,
      handle: { title: "Dashboard" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/today/pages/StaffTodayPage")).StaffTodayPage,
      }),
    },
    {
      path: "employees",
      handle: { title: "Employees" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/employees/pages/EmployeesPage")).EmployeesPage,
      }),
    },
    {
      path: "employees/new",
      handle: { title: "Register employee" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/employees/pages/RegisterEmployeePage"))
          .RegisterEmployeePage,
      }),
    },
    {
      path: "employees/import",
      handle: { title: "Import employees" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/employees/pages/ImportEmployeesPage")).ImportEmployeesPage,
      }),
    },
    {
      // Keyed on the shared `people.id`: an employee is a person (ADR-0017 decision 1), and the
      // core person page's "Also in: Staff" link lands here (`personClaims`).
      path: "employees/:personId",
      handle: { title: "Employee" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/employees/pages/EmployeePage")).EmployeePage,
      }),
    },
    {
      // The day drawer is `?person&day` on this page, not a route of its own: the grid behind it
      // must survive opening one day.
      path: "attendance",
      handle: { title: "Attendance" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/attendance/pages/StaffAttendancePage")).StaffAttendancePage,
      }),
    },
    {
      // The month (D wave): the overview, then one drill-down per metric (an unknown metric is a 404).
      path: "mis",
      handle: { title: "Monthly MIS" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/dashboard/pages/StaffMisPage")).StaffMisPage,
      }),
    },
    {
      path: "mis/:metric",
      handle: { title: "Monthly MIS" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/dashboard/pages/StaffMisMetricPage")).StaffMisMetricPage,
      }),
    },
    {
      path: "approvals",
      handle: { title: "Approvals" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/approvals/pages/ApprovalsPage")).ApprovalsPage,
      }),
    },
    {
      path: "requests",
      handle: { title: "Requests" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/requests/pages/RequestsPage")).RequestsPage,
      }),
    },
    {
      path: "mobile-punches",
      handle: { title: "Mobile punches" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/attendance/pages/MobilePunchesPage")).MobilePunchesPage,
      }),
    },
    // The Roster entry's address: not a page, it lands on Shifts, like `/reports` on Attendance.
    { path: "roster", element: <Navigate to={ROUTES.staffRosterShifts} replace /> },
    {
      path: "roster/shifts",
      handle: { title: "Shifts" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/roster/pages/ShiftsPage")).ShiftsPage,
      }),
    },
    {
      path: "roster/assignments",
      handle: { title: "Shift assignments" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/roster/pages/AssignmentsPage")).AssignmentsPage,
      }),
    },
    {
      path: "roster/holidays",
      handle: { title: "Holidays & week-offs" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/roster/pages/HolidaysPage")).HolidaysPage,
      }),
    },
    {
      path: "roster/overrides",
      handle: { title: "Day overrides" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/roster/pages/OverridesPage")).OverridesPage,
      }),
    },
    {
      path: "roster/comp-offs",
      handle: { title: "Comp-offs" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/roster/pages/CompOffsPage")).CompOffsPage,
      }),
    },
    {
      path: "periods",
      handle: { title: "Periods" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/periods/pages/PeriodsPage")).PeriodsPage,
      }),
    },
    {
      // `:month` is `YYYY-MM`, the period's own key on the api (`/staff/periods/:month/…`).
      path: "periods/:month",
      handle: { title: "Period" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/periods/pages/PeriodPage")).PeriodPage,
      }),
    },
    {
      // The module's own reports hub — never a card on the core `/reports` one (ADR-0017 addendum).
      path: "reports",
      handle: { title: "Staff reports" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/reports/pages/StaffReportsPage")).StaffReportsPage,
      }),
    },
    {
      path: "reports/:kind",
      handle: { title: "Staff report" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/reports/pages/StaffReportPage")).StaffReportPage,
      }),
    },
    {
      path: "settings",
      handle: { title: "Staff settings" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/settings/pages/StaffSettingsPage")).StaffSettingsPage,
      }),
    },
    {
      // The setup guide (lane G): `?step=<id>` opens one step and `?person=<id>` focuses steps 12–14 on one
      // employee. Inside the gated layout like every Staff page, so a lapsed add-on shows `AddonGate`; the
      // first-visit redirect that sends a reader here is the gate's, and it fires on the Dashboard only. The title
      // is the breadcrumb's ("Staff › Setup guide") and the sidebar entry's name; the page's own heading is
      // "Set up Staff".
      path: "setup",
      handle: { title: "Setup guide" },
      lazy: async () => ({
        Component: (await import("@/modules/staff/setup/pages/StaffSetupPage")).StaffSetupPage,
      }),
    },
  ],
};
